// EPS-4855: BAREM çoklu fiyat aralığı için ortak validation.
// EPS-4970: Standart Plan da aynı aralık yapısını kullanıyor, tek farkı aralığa
// düz fiyat yerine ORAN girilmesi. Kurallar birebir aynı olduğu için fonksiyon
// `priceField` ile parametrik hale getirildi.
//
// newPackagePage.jsx ve detail.jsx aynı kurallarda çalışsın diye buradan import.
//
// Her aralık (barem) için kurallar:
//   1. Tüm alanlar boşsa satır atlanır (submit'te BE'ye gönderilmez).
//   2. Kısmen dolu satırın min/max/(fiyat|oran)'ı tamamen doldurulmuş olmalı.
//   3. fiyat > 0 / oran 0 < r <= 100.
//   4. min < max.
// Aralıklar arası:
//   5. Çakışma yok (disjoint): A.max < B.min veya B.max < A.min.
//      Örn: 100-500 ile 400-1000 ÇAKIŞIR; 100-500 ile 501-1000 OK.
//
// Dönüş: Array<{ path, message }>. Boş array = geçerli.
//   - `path` RHF setError için kullanılır (örn "barems.0.minPrice").
//   - `message` toast ve inline error için kullanılır.
//   - İki path aynı mesajı paylaşabilir (min<max gibi iki alanı işaretlemek için).
//
// @param {Array} barems
// @param {Function} t i18n
// @param {Object} [options]
// @param {"planPrice"|"rate"} [options.priceField="planPrice"] hangi alanın fiyat rolünde olduğu
// @param {string} [options.fieldPrefix="barems"] RHF alan yolu öneki (Standart planda "rateBarems")
// @param {boolean} [options.requirePackageCode=false] paket kodu zorunlu mu (Standart planda evet —
//        plan seviyesinde kod yok, sigorta kanalına gidecek tek kaynak bu alan)

const isFilled = (v) => v !== undefined && v !== null && v !== "";

const rowSummary = (b, priceField) => {
  const hasMin = isFilled(b.minPrice);
  const hasMax = isFilled(b.maxPrice);
  const hasPrice = isFilled(b[priceField]);
  const hasPkg = !!b.packageCode && b.packageCode.length > 0;
  return {
    hasMin,
    hasMax,
    hasPrice,
    anyFilled: hasMin || hasMax || hasPrice || hasPkg,
    allRequired: hasMin && hasMax && hasPrice,
  };
};

// EPS-4970 revize: Standart Planda satış fiyatı alanı kaldırıldığı için fiyatın
// tek kaynağı oransal aralıklar — "en az bir dolu aralık" kontrolü iki sayfada
// da (oluşturma + düzenleme) aynı olmalı. validateBarems tamamen boş satırları
// atladığı için bu kontrol ondan ayrı duruyor.
export function hasFilledBarems(barems, priceField = "planPrice") {
  return (Array.isArray(barems) ? barems : []).some(
    (b) =>
      b &&
      isFilled(b.minPrice) &&
      isFilled(b.maxPrice) &&
      isFilled(b[priceField]),
  );
}

export function hasFilledRateBarems(barems) {
  return hasFilledBarems(barems, "rate");
}

export function validateBarems(barems, t, options = {}) {
  const priceField = options.priceField || "planPrice";
  const fieldPrefix = options.fieldPrefix || "barems";
  const requirePackageCode = options.requirePackageCode === true;
  const isRate = priceField === "rate";
  const list = Array.isArray(barems) ? barems : [];

  for (let i = 0; i < list.length; i++) {
    const b = list[i];
    const { hasMin, hasMax, hasPrice, anyFilled, allRequired } = rowSummary(
      b,
      priceField,
    );

    if (!anyFilled) continue;

    if (!allRequired) {
      const missing = !hasMin ? "minPrice" : !hasMax ? "maxPrice" : priceField;
      return [
        {
          path: `${fieldPrefix}.${i}.${missing}`,
          message: isRate
            ? t(
                "extendedWarranties.validateMsg.rateBaremRowIncomplete",
                "Aralık {{n}}: Minimum fiyat, Maksimum fiyat ve Oran doldurulmalı",
                { n: i + 1 },
              )
            : t(
                "extendedWarranties.validateMsg.baremRowIncomplete",
                "Aralık {{n}}: Minimum fiyat, Maksimum fiyat ve Plan fiyatı doldurulmalı",
                { n: i + 1 },
              ),
        },
      ];
    }

    if (Number(b[priceField]) <= 0) {
      return [
        {
          path: `${fieldPrefix}.${i}.${priceField}`,
          message: isRate
            ? t(
                "extendedWarranties.validateMsg.rateRequired",
                "Oran 0'dan büyük olmalıdır",
              )
            : t(
                "extendedWarranties.validateMsg.planPriceRequired",
                "Plan fiyatı 0'dan büyük olmalıdır",
              ),
        },
      ];
    }

    if (requirePackageCode && !(b.packageCode || "").trim()) {
      return [
        {
          path: `${fieldPrefix}.${i}.packageCode`,
          message: t(
            "extendedWarranties.validateMsg.packageCodeRequired",
            "Aralık {{n}}: Paket kodu zorunludur",
            { n: i + 1 },
          ),
        },
      ];
    }

    if (isRate && Number(b.rate) > 100) {
      return [
        {
          path: `${fieldPrefix}.${i}.rate`,
          message: t(
            "extendedWarranties.validateMsg.rateTooLarge",
            "Oran %100'den büyük olamaz",
          ),
        },
      ];
    }

    if (Number(b.minPrice) >= Number(b.maxPrice)) {
      const message = t(
        "extendedWarranties.validateMsg.minLessThanMax",
        "Minimum fiyat, maksimum fiyattan küçük olmalıdır",
      );
      return [
        { path: `${fieldPrefix}.${i}.minPrice`, message },
        { path: `${fieldPrefix}.${i}.maxPrice`, message },
      ];
    }
  }

  const filled = list
    .map((b, i) => ({ ...b, _idx: i }))
    .filter((b) => isFilled(b.minPrice) && isFilled(b.maxPrice));
  const sorted = [...filled].sort(
    (a, b) => Number(a.minPrice) - Number(b.minPrice),
  );
  for (let i = 0; i < sorted.length - 1; i++) {
    const curr = sorted[i];
    const next = sorted[i + 1];
    if (Number(curr.maxPrice) >= Number(next.minPrice)) {
      return [
        {
          path: `${fieldPrefix}.${next._idx}.minPrice`,
          message: t(
            "extendedWarranties.validateMsg.baremOverlap",
            "Aralık {{a}} ({{aMin}}-{{aMax}} ₺) ile Aralık {{b}} ({{bMin}}-{{bMax}} ₺) çakışıyor. Bir sonraki aralık {{nextMin}} ₺'den başlamalı.",
            {
              a: curr._idx + 1,
              aMin: curr.minPrice,
              aMax: curr.maxPrice,
              b: next._idx + 1,
              bMin: next.minPrice,
              bMax: next.maxPrice,
              nextMin: Number(curr.maxPrice) + 1,
            },
          ),
        },
      ];
    }
  }

  return [];
}
